<template>
  <div class="flex items-center">
    <button
      type="button"
      @click="!disabled && toggle()"
      :class="[
        'relative inline-flex flex-shrink-0 cursor-pointer border-2 border-transparent transition-colors duration-300 ease-in-out focus:outline-none p-0.5',
        square ? 'rounded-md' : 'rounded-full',
        disabled ? 'opacity-50 cursor-not-allowed' : '',
        !isHexColor && isModelValueTrue && !disabled ? `bg-${color}-600 dark:bg-${color}-500` : 'bg-gray-200 dark:bg-gray-600',
        sizeClasses.button
      ]"
      :style="buttonStyle"
      role="switch"
      :aria-checked="isModelValueTrue"
      :aria-disabled="disabled"
      :disabled="disabled"
    >
      <span class="sr-only">Toggle switch</span>
      <span
        :class="[
          'pointer-events-none inline-block transform bg-white dark:bg-gray-200 shadow-lg ring-0 transition duration-300 ease-in-out',
          square ? 'rounded' : 'rounded-full',
          isModelValueTrue ? translateClasses.on : translateClasses.off,
          sizeClasses.handle
        ]"
      >
        <span v-if="checkedIcon && isModelValueTrue" class="flex items-center justify-center h-full w-full">
          <q-icon :name="checkedIcon" class="text-gray-50 dark:text-gray-950"></q-icon>
        </span>
        <span v-if="uncheckedIcon && !isModelValueTrue" class="flex items-center justify-center h-full w-full">
          <q-icon :name="uncheckedIcon" class="text-gray-50 dark:text-gray-950"></q-icon>
        </span>
      </span>
    </button>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";

interface ToggleSwitchProps {
  modelValue: boolean | string | number;
  color?: string;
  size?: 'sm' | 'md' | 'lg';
  checkedIcon?: string;
  uncheckedIcon?: string;
  trueValue?: boolean | string | number;
  falseValue?: boolean | string | number;
  square?: boolean;
  disabled?: boolean;
}

const props = withDefaults(defineProps<ToggleSwitchProps>(), {
  color: "blue",
  size: "md",
  checkedIcon: "",
  uncheckedIcon: "",
  trueValue: true,
  falseValue: false,
  square: false,
  disabled: false
});

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean | string | number): void
}>();

const isModelValueTrue = computed(() => {
  return props.modelValue === props.trueValue;
});

const toggle = () => {
  if (!props.disabled) {
    emit("update:modelValue", isModelValueTrue.value ? props.falseValue : props.trueValue);
  }
};

const isHexColor = computed(() => {
  return /^#([0-9A-F]{3}){1,2}$/i.test(props.color);
});

const sizeClasses = computed(() => {
  switch (props.size) {
    case "sm":
      return {
        button: "h-7 w-12",
        handle: "h-5 w-5",
      };
    case "lg":
      return {
        button: "h-11 w-18",
        handle: "h-9 w-9",
      };
    case "md":
    default:
      return {
        button: "h-9 w-16",
        handle: "h-7 w-7",
      };
  }
});

const translateClasses = computed(() => {
  switch (props.size) {
    case "sm":
      return {
        on: "translate-x-5",
        off: "translate-x-0.5",
      };
    case "lg":
      return {
        on: "translate-x-7",
        off: "translate-x-0.5",
      };
    case "md":
    default:
      return {
        on: "translate-x-7",
        off: "translate-x-0.5",
      };
  }
});

const buttonStyle = computed(() => {
  if (isHexColor.value && isModelValueTrue.value && !props.disabled) {
    return {
      backgroundColor: props.color
    }
  } else if (isHexColor.value && isModelValueTrue.value && props.disabled) {
    return {
      backgroundColor: props.color,
      opacity: '0.5'
    }
  }
  return {}
})
</script>

<style scoped>
/* Optional: Add a subtle shadow effect to the toggle */
button {
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  padding: 2px;
}

/* Optional: Add a subtle scale effect on hover */
button:not(:disabled):hover {
  transform: scale(1.02);
  transition: transform 0.2s ease;
}

/* Optional: Add a subtle pulse animation when toggled */
@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(59, 130, 246, 0.5);
  }
  70% {
    box-shadow: 0 0 0 6px rgba(59, 130, 246, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(59, 130, 246, 0);
  }
}

button:not(:disabled):active span {
  animation: pulse 0.5s;
}

/* Dark mode adjustments */
@media (prefers-color-scheme: dark) {
  button {
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
  }
}
</style>
